import type {
  AgentConfig,
  AgentSlot,
  SlotStart,
  SlotStatus,
} from "../api/hooks/useAgentConfigs";

const COLD_SUFFIX = ":cold";

const day = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" }) : "";

/** A slot's name in a list: its config, and "cold start" where it began empty, since a
 * config can run both ways on one project. */
export const slotName = (slot: Pick<AgentSlot, "start" | "label">): string =>
  slot.start === "cold" ? `${slot.label} (cold start)` : slot.label;

/** Mirrors agent_configs.slot_key: "<config>" for a branch, "<config>:cold" cold. */
export const slotKey = (configKey: string, start: SlotStart): string =>
  start === "cold" ? `${configKey}${COLD_SUFFIX}` : configKey;

export const isColdSlot = (key: string): boolean => key.endsWith(COLD_SUFFIX);

/** The same name from a bare slot key ("sonnet-specialists:cold"), where only the key is
 * at hand; the key itself if the config is unknown here. */
export const slotKeyName = (key: string, configs: AgentConfig[] | undefined): string => {
  const cold = isColdSlot(key);
  const configKey = cold ? key.slice(0, -COLD_SUFFIX.length) : key;
  const config = configs?.find((c) => c.key === configKey);
  return config ? slotName({ label: config.label, start: cold ? "cold" : "branch" }) : key;
};

/** Where a slot's state came from: "branched from default on 20 Oct". */
export const slotOrigin = (slot: AgentSlot): string =>
  slot.start === "cold"
    ? `cold start on ${day(slot.created_at)}`
    : `branched from default on ${day(slot.branched_at ?? slot.created_at)}`;

/** "Active", "Paused: <reason>" or "Not available here: <reason>", for a project's slot
 * and for a project on the team page's list of a slot. */
export const slotStatusText = (
  status: SlotStatus,
  pausedReason: string | null,
  unavailableReason?: string | null,
): string => {
  const why = (reason: string | null | undefined) => (reason ? `: ${reason}` : "");
  if (status === "unavailable") return `Not available here${why(unavailableReason)}`;
  return status === "paused" ? `Paused${why(pausedReason)}` : "Active";
};

/** Whether this environment may run the slot; one it may not can only be removed. */
export const slotAvailable = (slot: AgentSlot): boolean =>
  slot.available !== false && slot.status !== "unavailable";

/** On a cross-project page, which reads default only, once other slots exist. */
export const DEFAULT_ONLY_NOTE = "Shows the default configuration.";
